import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

/**
 * PortalPopups
 *
 * Rendert unseen Popups einzeln nacheinander (Welcome vor Campaign).
 *
 * Zwei unterschiedliche Gating-Modelle:
 *  * ``welcome``  — Backend führt persistentes ,,seen-Version"-Tracking.
 *                   Ein Kunde bekommt jede veröffentlichte Version genau
 *                   einmal — üblich der einmalige Willkommensgruß nach
 *                   Registrierung.
 *  * ``campaign`` — bewusst KEIN persistentes Tracking. Gating erfolgt
 *                   ausschließlich clientseitig via ``sessionStorage``:
 *                   * Bei jedem echten Login (``AuthProvider.login``)
 *                     wird der Marker gelöscht → Werbung darf erneut
 *                     erscheinen.
 *                   * Beim Anzeigen der Werbung setzen wir den Marker →
 *                     Reload / Navigation innerhalb desselben Login-
 *                     Zyklus zeigt sie NICHT nochmal.
 *                   * Bei Logout wird der Marker ebenfalls gelöscht.
 *
 * UX:
 *  * ~700 ms bewusste Verzögerung nach Portal-Load, damit die Werbung
 *    nicht Teil des initialen Renderns wirkt, sondern eine bewusst
 *    inszenierte Einblendung.
 *  * Sanfter Fade-/Zoom-In (~300 ms) via ``tailwindcss-animate``.
 *  * Reihenfolge Welcome → 2 s Pause → Campaign bleibt bestehen; die
 *    2 s decken bereits Fade-Out + neue Fade-In-Phase des Overlays ab.
 */
const INITIAL_DELAY_MS = 700;
const ADVANCE_DELAY_MS = 2000;
const AD_SESSION_KEY = "loop.ad_shown_session";

function readAdShown() {
  try { return sessionStorage.getItem(AD_SESSION_KEY) === "1"; } catch { return false; }
}
function writeAdShown() {
  try { sessionStorage.setItem(AD_SESSION_KEY, "1"); } catch { /* ignore */ }
}

export default function PortalPopups() {
  const q = useQuery({
    queryKey: ["portal-popups"],
    queryFn: async () => (await api.get("/customer/popups/current")).data,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  // Filter: Werbung wird pro Login-Zyklus nur einmal angezeigt. Reload
  // innerhalb der Session (``sessionStorage`` überlebt Reload) darf sie
  // NICHT erneut zeigen.
  const popups = useMemo(() => {
    const arr = q.data || [];
    const adShown = readAdShown();
    return adShown ? arr.filter((p) => p.kind !== "campaign") : arr;
  }, [q.data]);

  const [idx, setIdx] = useState(0);
  const [pausing, setPausing] = useState(false);
  const [ready, setReady] = useState(false);
  const [closing, setClosing] = useState(false);

  // 700 ms bewusste Einblendungs-Verzögerung nach Portal-Ready.
  useEffect(() => {
    if (!popups.length) return;
    setReady(false);
    const t = setTimeout(() => setReady(true), INITIAL_DELAY_MS);
    return () => clearTimeout(t);
  }, [popups.length]);

  useEffect(() => { setIdx(0); setPausing(false); setClosing(false); }, [popups.length]);

  // Sobald eine Werbung tatsächlich sichtbar wird, markieren wir sie im
  // Login-Zyklus als gezeigt. Damit verhindert ein sofortiger Reload das
  // erneute Erscheinen ebenfalls (nicht erst der Close-Klick).
  const current = popups[idx];
  useEffect(() => {
    if (ready && !pausing && !closing && current?.kind === "campaign") {
      writeAdShown();
    }
  }, [ready, pausing, closing, current]);

  if (!popups.length || idx >= popups.length || pausing || !ready) return null;
  const p = current;

  async function markSeenAndAdvance() {
    // Sanftes Ausblenden (~180 ms), dann State-Übergang. Ohne unnötige
    // Komplexität — kein Presence-Manager, nur ein kurzer Timer.
    setClosing(true);
    await new Promise((r) => setTimeout(r, 180));

    if (p.kind === "welcome") {
      // Nur Welcome bekommt persistentes ,,seen"-Tracking.
      try { await api.post(`/customer/popups/${p.kind}/seen`, { version: p.version }); }
      catch { /* still advance */ }
    } else if (p.kind === "campaign") {
      // Werbung: nur session-scoped Marker (bereits per Mount gesetzt).
      writeAdShown();
    }

    if (idx + 1 < popups.length) {
      setPausing(true);
      setIdx((i) => i + 1);
      setClosing(false);
      setTimeout(() => setPausing(false), ADVANCE_DELAY_MS);
    } else {
      setIdx((i) => i + 1);
      setClosing(false);
    }
  }

  function handleCta() {
    if (!p.cta_url) return;
    const url = p.cta_url.trim();
    if (url.startsWith("/")) window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
    markSeenAndAdvance();
  }

  // Sanfte Fade+Zoom-Animation aus ``tailwindcss-animate``. In-Phase auf
  // Mount (~300 ms), Out-Phase auf Close (~180 ms). Overlay-Backdrop
  // faded parallel.
  const enterOverlay = "animate-in fade-in-0 duration-300";
  const exitOverlay = "animate-out fade-out-0 duration-200";
  const enterCard = "animate-in fade-in-0 zoom-in-95 duration-300 ease-out";
  const exitCard = "animate-out fade-out-0 zoom-out-95 duration-200 ease-in";

  return (
    <div
      className={`fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-3 sm:p-6 ${closing ? exitOverlay : enterOverlay}`}
      data-testid={`popup-${p.kind}`}
      onClick={(e) => { if (e.target === e.currentTarget) markSeenAndAdvance(); }}
    >
      <div
        className={`bg-zinc-950 border border-zinc-800 rounded-lg w-[95vw] sm:w-[92vw] lg:w-[80vw] xl:w-[75vw] max-w-[1400px] max-h-[95vh] overflow-hidden shadow-2xl flex flex-col ${closing ? exitCard : enterCard}`}
      >
        {p.image_url && (
          <div className="w-full bg-zinc-950 flex items-center justify-center shrink-0">
            <img
              src={p.image_url}
              alt=""
              draggable={false}
              className="w-full max-h-[55vh] sm:max-h-[60vh] lg:max-h-[65vh] object-contain"
              data-testid={`popup-${p.kind}-image`}
            />
          </div>
        )}
        <div className="p-5 sm:p-8 space-y-3 sm:space-y-4 overflow-y-auto">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              {p.title && (
                <div
                  className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight leading-tight break-words"
                  data-testid={`popup-${p.kind}-title`}
                >
                  {p.title}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid={`popup-${p.kind}-close`}
              onClick={markSeenAndAdvance}
              aria-label="Schließen"
              className="w-10 h-10 -mr-2 -mt-2 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 shrink-0 transition-colors"
            >
              <X size={22} />
            </button>
          </div>
          {p.body && (
            <div
              className="text-sm sm:text-base text-zinc-300 whitespace-pre-wrap leading-relaxed"
              data-testid={`popup-${p.kind}-body`}
            >
              {p.body}
            </div>
          )}
          {p.kind === "campaign" && p.cta_url && (
            <Button
              onClick={handleCta}
              data-testid={`popup-${p.kind}-cta`}
              className="bg-cyan-400 hover:bg-cyan-300 w-full mt-2 h-11 sm:h-12 text-base font-bold"
            >
              {p.cta_label || "Mehr erfahren"} <ExternalLink size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
